import { useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";

import { ToastService } from "@/components/ToastService";
import { TOAST_ID } from "@/constants";
import { ColoredNetworkIconMap } from "@/constants/map";
import { Network } from "@/interfaces/network";
import { NearAirdropContract } from "@/services/contracts/near/Airdrops";
import { FungibleTokenContract } from "@/services/contracts/near/FungibleToken";
import { NonFungibleTokenContract } from "@/services/contracts/near/NonFungibleToken";
import { retrieveTokensMetadata } from "@/services/helpers/near";
import { AirdropResponse } from "@/services/rpc/near";
import { parseTokenAmount } from "@/utils";
import { formatOnchainNft } from "@/utils/airdrop";
import { config as serviceConfig } from "src/services/config";

import { useAirdrops } from "./store/useAirdrops";
import { UploadType, useStore } from "./store/useStore";
import { useTokens } from "./store/useTokens";

export const useLoadAirdrops = () => {
  const config = useSWRConfig();
  const { tokens, setTokens } = useTokens((store) => ({ tokens: store.tokens, setTokens: store.setTokens }));
  const setAirdrops = useAirdrops((store) => store.setAirdrops);
  const setHapiBalance = useAirdrops((store) => store.setHapiBalance);

  const { user, network, manualUserUpdate, updateUploadType } = useStore((store) => ({
    user: store.user,
    network: store.network,
    manualUserUpdate: store.manualUserUpdate,
    updateUploadType: store.updateUploadType,
  }));

  const swrKey = `AIRDROPS/${network}`;
  const icon = ColoredNetworkIconMap[network];

  return useSWRImmutable<Array<AirdropResponse> | undefined>(
    swrKey,
    async () => {
      ToastService.loading({ id: TOAST_ID.AIRDROPS, text: "Loading Airdrops" });
      const nftContract = new NonFungibleTokenContract(serviceConfig.near.nft);
      const nftIdsBackend = user.networksData.NEAR.nft?.array?.map(({ nftId }) => nftId);
      let nftIds = nftIdsBackend;

      if (!nftIdsBackend && user.nearAccount) {
        updateUploadType(UploadType.Offchain);
        const nftOnchain = await nftContract.getUserNFT(user.nearAccount ?? "", 100, "0");
        const nftIdsOnchain = nftOnchain?.map((el) => el.token_id);
        nftIds = nftIdsOnchain;
        const userScore = await NearAirdropContract.get_user_score(user.nearAccount);
        const onchainFormattedNfts = formatOnchainNft(nftOnchain);
        manualUserUpdate(
          userScore?.trust_score_record.score ?? 0,
          userScore?.trust_score_record.valid_until ?? 0,
          onchainFormattedNfts,
        );
      }

      switch (network) {
        case Network.NEAR: {
          if (!user.nearAccount || !nftIds) return;
          const airdrops = await NearAirdropContract.get_airdrops(user.nearAccount, nftIds);
          const minAllowedScore = await NearAirdropContract.get_available_trust_score();
          const hapiTokenContract = new FungibleTokenContract(serviceConfig.near.hapiTokenAddress);
          const balance = await hapiTokenContract.getBalance({ accountId: user.nearAccount });
          if (!airdrops) return;
          const airdropsUpdated = await Promise.all(
            airdrops.map(async (airdrop) => {
              const userClaimedAmount = await NearAirdropContract.get_user_number_of_claims(
                airdrop.id,
                user.nearAccount,
              );
              return { ...airdrop, userClaimsCount: parseTokenAmount(userClaimedAmount, 18) };
            }),
          );
          if (!airdropsUpdated) return;
          const tokenAddresses = [...new Set(airdropsUpdated.map(({ token_id }) => token_id))];
          const tokensByNetwork = tokens[Network.NEAR];
          const savedTokens = Object.values(tokensByNetwork);

          const newTokenAddresses = tokenAddresses.filter(
            (tokenAddress) => !savedTokens.some((savedToken) => savedToken.contractId === tokenAddress),
          );

          if (newTokenAddresses.length > 0) {
            const newTokens = await retrieveTokensMetadata(newTokenAddresses);
            setTokens(Network.NEAR, { ...tokensByNetwork, ...newTokens });
          }
          setHapiBalance(balance ?? null);
          setAirdrops(Network.NEAR, airdropsUpdated, minAllowedScore || 0);
          return airdrops;
        }

        default:
          return;
      }
    },
    {
      onSuccess() {
        ToastService.clear(TOAST_ID.AIRDROPS);
      },
      onError(err) {
        ToastService.update({ id: TOAST_ID.AIRDROPS, text: err.message, icon });
        console.error(`Error, while loading airdrops ${network} \n\n`, err);
      },
      revalidateOnMount: !config.cache.get(swrKey),
    },
  );
};
